import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Newspaper } from "lucide-react";
import api from "@/lib/api";
import { applySeo } from "@/lib/seo";

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };

// Minimal, safe markdown renderer (headings, lists, bold, paragraphs).
function renderInline(text, keyBase) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((seg, i) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={`${keyBase}-${i}`}>{seg.slice(2, -2)}</strong>
      : <span key={`${keyBase}-${i}`}>{seg}</span>);
}
function Markdown({ content }) {
  const lines = (content || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = (k) => { if (list.length) { blocks.push(<ul key={`ul-${k}`} className="list-disc pl-6 space-y-1 my-3 text-slate-700">{list}</ul>); list = []; } };
  lines.forEach((ln, i) => {
    if (ln.startsWith("### ")) { flush(i); blocks.push(<h3 key={i} className="text-lg font-bold mt-6 mb-2">{renderInline(ln.slice(4), i)}</h3>); }
    else if (ln.startsWith("## ")) { flush(i); blocks.push(<h2 key={i} className="text-2xl font-black mt-8 mb-3 tracking-tight">{renderInline(ln.slice(3), i)}</h2>); }
    else if (/^\d+\.\s/.test(ln)) { list.push(<li key={i}>{renderInline(ln.replace(/^\d+\.\s/, ""), i)}</li>); }
    else if (ln.startsWith("- ")) { list.push(<li key={i}>{renderInline(ln.slice(2), i)}</li>); }
    else if (ln.trim()) { flush(i); blocks.push(<p key={i} className="text-slate-700 leading-relaxed my-3">{renderInline(ln, i)}</p>); }
  });
  flush("end");
  return <div data-testid="news-content">{blocks}</div>;
}

export default function NewsArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    return applySeo({
      title: `${post.title} | OXO.ro`,
      description: post.meta_description || post.excerpt,
      image: post.cover_image ? (post.cover_image.startsWith("http") ? post.cover_image : `${process.env.REACT_APP_BACKEND_URL}${post.cover_image}`) : undefined,
      type: "article",
    });
  }, [post]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="news-not-found">
      <p className="text-slate-500">Articolul nu a fost găsit.</p>
      <Link to="/news" className="inline-block mt-4 text-orange-600 font-semibold hover:underline">Înapoi la știri</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-slate-400">Se încarcă...</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 py-12" data-testid="news-article-page">
      <Link to="/news" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-orange-600 mb-6 transition-colors" data-testid="back-to-news">
        <ArrowLeft className="w-4 h-4" /> Știri
      </Link>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-orange-600 flex items-center gap-1.5"><Newspaper className="w-3.5 h-3.5" /> {post.language}</span>
        <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar className="w-3 h-3" /> {fmtDate(post.created_at)}</span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-6">{post.title}</h1>
      {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full h-64 object-cover mb-8" />}
      <Markdown content={post.content} />
    </article>
  );
}
